/**
 * SETTING A CREDENTIAL FROM A MAILED TOKEN — the invitation, and the password reset.
 *
 * One screen for two routes, because they are the same act performed by two people at two moments:
 * somebody holding a token that reached their mailbox types a password, and an account ends up with
 * a first factor. `auth.invitation.accept` creates the account; `auth.password.reset_complete`
 * replaces the credential on one that exists. Everything else about the two is identical, including
 * the parts that matter most.
 *
 * ── NEITHER ONE ISSUES A SESSION, AND THAT IS THE WHOLE POINT ──────────────────────────────────
 *
 * `auth.password.reset_complete` says so in its own declaration: *"a completed reset re-establishes
 * the FIRST factor only, and an enrolled account must still present its second. Otherwise 'forgot
 * password' is a documented self-service route around MFA, and the mailbox becomes the privileged
 * boundary the Owner said it must not be."* `auth.invitation.accept` issues none either, so the
 * credential an invitee just chose is exercised once before it is trusted.
 *
 * So this screen NEVER lands anybody in the product. Both outcomes end at sign-in, and the screen
 * says that before the click rather than leaving somebody to wonder why they are not signed in.
 *
 * ── WHAT IT MAY SAY ABOUT A TOKEN: ALMOST NOTHING ──────────────────────────────────────────────
 *
 * Expired, revoked, already used and never existed are ONE answer, deliberately — a caller must not
 * be able to use this endpoint to discover which tokens exist. The single exception is
 * `password_too_short`, and the reason it is safe is structural: `evaluateRedemption` checks the
 * password length BEFORE it looks at the invitation, so that answer is identical for a live token,
 * an expired one and one that never existed. Collapsing it into the uniform failure would leave an
 * invitee who typed eleven characters holding a LIVE invitation, told only that it is "not valid",
 * with a support call as the only way forward.
 *
 * G-15 and G-10 in the ledger are the two gaps this closes: an invitation minted and delivered with
 * no browser path to redeem it, and *"a lost authenticator with spent recovery codes is a permanent
 * lockout"*.
 */

import { useState } from 'react';
import type { FormEvent } from 'react';

import { AppShell } from '../shell/AppShell';
import { StateBlock } from '../components/StateBlock';
import { callOperation } from '../api/operations';
import { routes } from '../routes';

/** Which of the two acts this is. The screen states it; nothing infers it from the token. */
export type CredentialSetupKind = 'invitation' | 'password_reset';

export interface CredentialSetupScreenProps {
  readonly kind: CredentialSetupKind;
  /** From `?token=` in the mailed link. `null` when somebody reached the address by hand. */
  readonly token: string | null;
}

interface Copy {
  readonly titleHe: string;
  readonly leadHe: string;
  readonly submitHe: string;
  readonly doneTitleHe: string;
  readonly doneBodyHe: string;
}

/** The Hebrew for each act, side by side, so the two cannot quietly diverge in tone. */
export const CREDENTIAL_SETUP_HE: Readonly<Record<CredentialSetupKind, Copy>> = {
  invitation: {
    titleHe: 'קבלת ההזמנה',
    leadHe: 'בחר סיסמה לחשבון שלך. ההזמנה אינה מקנה הרשאות — מנהל המשרד מעניק תפקיד וגישה לחברות בנפרד.',
    submitHe: 'יצירת החשבון',
    doneTitleHe: 'החשבון נוצר',
    // Said before anybody wonders why they are not inside. No session is issued here, by design.
    doneBodyHe:
      'החשבון נוצר עם הסיסמה שבחרת. לא בוצעה כניסה אוטומטית — יש להתחבר עכשיו עם הסיסמה הזו.',
  },
  password_reset: {
    titleHe: 'קביעת סיסמה חדשה',
    leadHe: 'בחר סיסמה חדשה לחשבון שלך.',
    submitHe: 'קביעת הסיסמה',
    doneTitleHe: 'הסיסמה עודכנה',
    doneBodyHe:
      'הסיסמה החדשה נשמרה. לא בוצעה כניסה אוטומטית, ואם מוגדר לחשבון אימות דו-שלבי — הוא עדיין נדרש. איפוס סיסמה אינו מדלג על הגורם השני.',
  },
};

/** Every refusal that is not `password_too_short`. One sentence, for four different reasons. */
const NOT_VALID_HE =
  'הקישור אינו תקף. ייתכן שפג תוקפו, שכבר נעשה בו שימוש או שבוטל. אין לנו דרך לומר במה מהם מדובר, ולכן צריך לבקש קישור חדש.';

const TOO_SHORT_HE =
  'הסיסמה שנבחרה קצרה מדי. הקישור עדיין תקף — אפשר לבחור סיסמה ארוכה יותר ולנסות שוב באותו מסך.';

type Outcome =
  | { readonly kind: 'done' }
  | { readonly kind: 'too_short' }
  | { readonly kind: 'not_valid' };

export function CredentialSetupScreen({ kind, token }: CredentialSetupScreenProps): JSX.Element {
  const [password, setPassword] = useState('');
  const [sending, setSending] = useState(false);
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const copy = CREDENTIAL_SETUP_HE[kind];

  function submit(e: FormEvent): void {
    e.preventDefault();
    if (token === null || sending) return;
    setSending(true);
    setOutcome(null);

    // TWO LITERAL OPERATION IDS, on two branches. A single `callOperation(idFor(kind), …)` would be
    // the computed id G-03 refuses — one call site standing for two routes, invisible to the scan.
    const call =
      kind === 'invitation'
        ? callOperation('auth.invitation.accept', { body: { token, password } })
        : callOperation('auth.password.reset_complete', { body: { token, password } });

    void call
      .then(
        () => setOutcome({ kind: 'done' }),
        (err: unknown) => {
          setOutcome({ kind: isTooShort(err) ? 'too_short' : 'not_valid' });
        },
      )
      .finally(() => setSending(false));
  }

  return (
    <AppShell
      titleHe={copy.titleHe}
      provenanceHe="הסיסמה נבדקת ונשמרת בשרת. הדפדפן אינו שומר אותה ואינו שולח אותה לשום מקום אחר."
    >
      <p className="screen-lead">{copy.leadHe}</p>

      {token === null ? (
        <StateBlock
          titleHe="אין קישור תקף"
          // Nothing was checked, and saying "the link is invalid" would be a claim about a link
          // nobody supplied.
          bodyHe="הגעת למסך הזה בלי קישור, ולכן אין מה לבדוק. יש לפתוח את הקישור שנשלח אליך בדואר."
          tone="unavailable"
          nextStep={
            <a className="btn" href={routes.signIn}>
              מעבר להתחברות
            </a>
          }
        />
      ) : outcome?.kind === 'done' ? (
        <StateBlock
          titleHe={copy.doneTitleHe}
          bodyHe={copy.doneBodyHe}
          nextStep={
            <a className="btn btn--primary" href={routes.signIn}>
              מעבר להתחברות
            </a>
          }
        />
      ) : (
        <form className="form" onSubmit={submit}>
          <label className="field">
            <span className="field__label">סיסמה חדשה</span>
            <input
              className="field__input"
              type="password"
              dir="ltr"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.currentTarget.value)}
              required
            />
            {/*
              NO LENGTH IS STATED HERE. The server owns the rule, and a number typed into a screen
              is a second copy of it that goes stale the day the rule changes — and reads as a
              promise the server never made. The refusal names the problem when it happens.
            */}
            <span className="field__hint">
              הסיסמה נבדקת בשרת. אם היא קצרה מדי, נאמר לך זאת והקישור יישאר תקף.
            </span>
          </label>

          <div className="actions-row">
            <button type="submit" className="btn btn--primary" disabled={sending}>
              {sending ? 'שולח…' : copy.submitHe}
            </button>
          </div>
        </form>
      )}

      {outcome?.kind === 'too_short' ? (
        <StateBlock titleHe="הסיסמה קצרה מדי" bodyHe={TOO_SHORT_HE} tone="error" />
      ) : null}

      {outcome?.kind === 'not_valid' ? (
        <StateBlock
          titleHe="הקישור אינו תקף"
          bodyHe={NOT_VALID_HE}
          tone="error"
          nextStep={
            <a className="btn" href={routes.signIn}>
              מעבר להתחברות
            </a>
          }
        />
      ) : null}
    </AppShell>
  );
}

/**
 * The ONE distinguished refusal, recognised by its declared code and nothing else.
 *
 * Read narrowly on purpose: anything this does not recognise falls to the uniform answer, so a new
 * server-side refusal cannot start disclosing something by being rendered with its own words here.
 */
function isTooShort(err: unknown): boolean {
  const body: unknown = (err as { body?: unknown } | null)?.body;
  return (body as { error?: unknown } | null)?.error === 'password_too_short';
}
